import { Button } from '@/atoms/Button';
import { Container } from '@/atoms/Container';
import { FlexRow } from '@/atoms/FlexRow';
import {
  ButtonType,
  Card,
  CardSize,
  CardType,
  TagPosition,
} from '@/components/Card';
import { Gallery } from '@/components/Gallery';
import { LocationMap } from '@/components/LocationMap';
import { PaginationRoutes } from '@/components/PaginationRoutes';
import { Placements } from '@/components/Placements';
import { MobileEffect, SliderBlock } from '@/components/SliderBlock';
import { Tickets } from '@/components/Tickets';
import { BLACK_COLOR } from '@/constants';
import { useAdaptiveWidth } from '@/hooks/useAdaptiveWidth';
import { useEnhancedRouter } from '@/hooks/useEnhancedRouter';
import { useShowMore } from '@/hooks/useShowMore';
import { Layout } from '@/layout';
import { fetchData } from '@/services/fetchData';
import {
  EventType,
  LocationType,
  ObjectItemType,
  Palettes,
  SettingsType,
  Variants,
} from '@/types';
import {
  formatCafesAndRestaurants,
  formatThematicStores,
  formatThematicZones,
} from '@/utils/formatTexts';
import { getTextColor } from '@/utils/getTextColor';
import { parseText } from '@/utils/parseText';
import InnerHTML from 'dangerously-set-html-content';
import { GetServerSidePropsContext, InferGetServerSidePropsType } from 'next';
import { useEffect } from 'react';
import styles from './Object.module.scss';

enum BLOCK_ARRAY_ELEMENT {
  Entertainments = 'entertainments',
  Tickets = 'tickets',
  Locations = 'locations',
  Events = 'events',
  Placements = 'placements',
  Map = 'map',
  Restaurants = 'restaurants',
  Shops = 'shops',
}

const DEFAULT_BLOCK_ARRAY = [
  BLOCK_ARRAY_ELEMENT.Entertainments,
  BLOCK_ARRAY_ELEMENT.Tickets,
  BLOCK_ARRAY_ELEMENT.Locations,
  BLOCK_ARRAY_ELEMENT.Events,
  BLOCK_ARRAY_ELEMENT.Placements,
  BLOCK_ARRAY_ELEMENT.Map,
  BLOCK_ARRAY_ELEMENT.Restaurants,
  BLOCK_ARRAY_ELEMENT.Shops,
];

const getBlockStructure = (blocks: string[]) => {
  if (blocks && Array.isArray(blocks)) {
    const blockMapping: { [key: string]: string } = {
      entertainments: BLOCK_ARRAY_ELEMENT.Entertainments,
      tickets: BLOCK_ARRAY_ELEMENT.Tickets,
      locations: BLOCK_ARRAY_ELEMENT.Locations,
      events: BLOCK_ARRAY_ELEMENT.Events,
      placements: BLOCK_ARRAY_ELEMENT.Placements,
      map: BLOCK_ARRAY_ELEMENT.Map,
      restaurants: BLOCK_ARRAY_ELEMENT.Restaurants,
      shops: BLOCK_ARRAY_ELEMENT.Shops,
    };

    const orderedBlocks = blocks
      .map((block) => blockMapping[block])
      .filter(Boolean);

    const missingBlocks = DEFAULT_BLOCK_ARRAY.filter(
      (block) => !orderedBlocks.includes(block),
    );

    return [...orderedBlocks, ...missingBlocks];
  }

  return DEFAULT_BLOCK_ARRAY;
};

const Object = ({
  menu,
  content,
  translations,
  common,
  customTranslations,
}: InferGetServerSidePropsType<typeof getServerSideProps>) => {
  const itemData: ObjectItemType = content.item;
  const { getMore, items, allShown } = useShowMore(
    itemData.locations,
    4,
    `object_${itemData.code}`,
  );
  const { isMobile } = useAdaptiveWidth();
  const { push, asPath, locale } = useEnhancedRouter();

  const orderedBlocks = getBlockStructure(itemData.blocks);

  const handleEventsClick = () => {
    if (asPath === '/objects/concert-hall') {
      push('https://moscow-hall.ru/events');
      return;
    }

    push('/events');
  };

  useEffect(() => {
    const color = itemData.colorStyles ?? BLACK_COLOR;

    if (color) {
      const styleTag = document.createElement('style');
      styleTag.textContent = getTextColor(color);
      document.head.appendChild(styleTag);

      return () => {
        document.head.removeChild(styleTag);
      };
    }
  }, [itemData.colorStyles]);

  return (
    <Layout data={menu} meta={content.meta}>
      <main className={styles.page}>
        <PaginationRoutes
          routes={[
            {
              link: '/objects',
              text: common.linkObjects,
            },
            {
              link: `/objects/${itemData.code}`,
              text: itemData.name,
            },
          ]}
        />
        <Gallery
          galleryMob={itemData.galleryMob}
          gallery={itemData.gallery}
          title={itemData.name}
          alert={itemData.alert}
        />
        {itemData.text && (
          <InnerHTML html={itemData.text} className="info" allowRerender />
        )}
        <div dangerouslySetInnerHTML={{ __html: itemData.styles }} />
        {orderedBlocks.map((block) => {
          switch (block) {
            case 'entertainments':
              return (
                <>
                  <SliderBlock
                    items={itemData.entertainments}
                    cardType={CardType.Filled}
                    button={
                      customTranslations.entertainmentsButton ??
                      translations.entertainmentsButton
                    }
                    link="/entertainments"
                    size={CardSize.M}
                    title={
                      customTranslations.entertainmentsTitle ??
                      translations.entertainmentsTitle
                    }
                    tagPosition={TagPosition.Top}
                    mobileEffect={MobileEffect.Column}
                    buttonType={ButtonType.Arrow}
                  />
                </>
              );
            case 'tickets': {
              const ticketsTranslations = customTranslations as unknown as {
                ticketsTitle?: string;
              };
              const ticketsTitle =
                ticketsTranslations.ticketsTitle ?? common.ticketsTitle;

              return (
                <>
                  <Tickets tickets={itemData.tickets} title={ticketsTitle} />
                </>
              );
            }
            case 'locations':
              return (
                <>
                  {itemData.locations && itemData.locations.length > 0 && (
                    <Container
                      title={formatThematicZones(
                        itemData.locations.length,
                        locale,
                      )}
                    >
                      <FlexRow>
                        {items.map((item: LocationType) => (
                          <Card
                            key={item.id}
                            picture={item.picture}
                            name={item.name}
                            code={item.code}
                            cardType={CardType.Panel}
                            size={CardSize.SM}
                            link={`/locations/${item.code}`}
                            tagPosition={TagPosition.Top}
                            typeName={item.type}
                            pageMissing={item.pageMissing}
                          />
                        ))}
                      </FlexRow>
                      {!allShown && itemData.locations.length > 4 && (
                        <Button
                          text={common.showMoreButton}
                          onClick={getMore}
                          variant={Variants.Outline}
                          palette={Palettes.Secondary}
                          className={styles.more}
                        />
                      )}
                    </Container>
                  )}
                </>
              );
            case 'events':
              return (
                <>
                  {itemData.events && itemData.events.length > 0 && (
                    <Container
                      title={
                        customTranslations.eventsTitle ??
                        translations.eventsTitle
                      }
                    >
                      <FlexRow>
                        {itemData.events.slice(0, 4).map((item: EventType) => (
                          <Card
                            key={item.id}
                            picture={item.picture}
                            name={item.name}
                            code={item.code}
                            cardType={CardType.Panel}
                            size={CardSize.SM}
                            dateFrom={item.dateFrom}
                            dateTo={item.dateTo}
                            link={`/events/${item.code}`}
                            tagPosition={TagPosition.Top}
                            info={item.info}
                            button={item.button}
                            noType
                            buttonLink={item.buttonLink}
                            twoRowTitle
                            withButton
                            pageMissing={item.pageMissing}
                            duplicateCardLink
                          />
                        ))}
                      </FlexRow>
                      <Button
                        text={
                          customTranslations.eventsButton ??
                          translations.eventsButton
                        }
                        onClick={handleEventsClick}
                        variant={Variants.Outline}
                        palette={Palettes.Secondary}
                        className={styles.more}
                      />
                    </Container>
                  )}
                </>
              );
            case 'placements':
              return (
                <>
                  {itemData.placements && itemData.placements.length > 0 && (
                    <Placements items={itemData.placements} />
                  )}
                </>
              );
            case 'map':
              return (
                <>
                  {itemData.location && (
                    <LocationMap
                      img={itemData.location.picture}
                      file={itemData.location.file}
                      mobileImg={itemData.location.pictureMob}
                      title={
                        customTranslations.mapTitle ?? translations.mapTitle
                      }
                      internal
                    />
                  )}
                </>
              );
            case 'restaurants':
              return (
                <>
                  {itemData.rests && itemData.rests.length > 0 && (
                    <SliderBlock
                      items={itemData.rests}
                      cardType={CardType.Panel}
                      button={parseText(common.restsButton)}
                      link={`/objects/${itemData.code}/restaurants`}
                      size={CardSize.XS}
                      title={
                        customTranslations.restsTitle ??
                        formatCafesAndRestaurants(itemData.rests.length, locale)
                      }
                      mobileEffect={MobileEffect.Slider}
                    />
                  )}
                </>
              );
            case 'shops':
              return (
                <>
                  {itemData.shops && itemData.shops.length > 0 && (
                    <SliderBlock
                      items={itemData.shops}
                      cardType={CardType.Panel}
                      button={
                        isMobile
                          ? (customTranslations.shopsButtonMob ??
                            translations.shopsButtonMob)
                          : common.shopsButton
                      }
                      link={`/objects/${itemData.code}/shops`}
                      size={CardSize.XS}
                      title={
                        customTranslations.shopsTitle ??
                        formatThematicStores(itemData.shops.length, locale)
                      }
                      mobileEffect={MobileEffect.Slider}
                    />
                  )}
                </>
              );
            default:
              return null;
          }
        })}
      </main>
    </Layout>
  );
};

export async function getServerSideProps({
  params,
  locale,
  resolvedUrl,
}: GetServerSidePropsContext) {
  const paramsId = params?.object;

  const content = await fetchData({
    url: `/content/our-object/${paramsId}/view`,
    locale,
  });

  const menu: SettingsType = await fetchData({
    url: `/settings/view`,
    locale,
  });

  const isError = !content || content.code !== 200;
  if (isError) return { notFound: true };

  const objectPath = resolvedUrl.split('?')[0] as '/objects/[object]';
  const translations = menu.translations['/objects/[object]'];
  const customTranslations = menu.translations[objectPath]
    ? menu.translations[objectPath]
    : translations;

  return {
    props: {
      content,
      menu,
      translations,
      customTranslations,
      common: menu.translations.common,
      resolvedUrl,
    },
  };
}

export default Object;
